import { auth } from "./firebase";
import { crmGetContactScansCallable, crmGetScanViewerDataCallable } from "./crmApi";
import { contactUserKeysForRollup, scanDocTimestampMs } from "./contactScanRollup";
import { productDocUserRef } from "./productAnalyticsMetrics";
import { scanLabelFromDoc, formatSqft, extractSqftFromDoc, workspaceIdFromDoc } from "./scanSqft";
import { decodeImageBitmap, sniffImageMime } from "./imageDecode";

const PROJECT_ID = "alpine-realm-473117-f9";
const REGION = "us-central1";
/**
 * Sent with crmGetScanViewerData: 2 = this client loads recon.obj with one
 * material per atlas (scanModelLoader). The server keeps recon.obj from
 * clients that do not send it.
 */
export const SCAN_VIEWER_API = 2;

export function scanFileProxyUrl(scanId, fileId, { extract } = {}) {
  const url = new URL(`https://${REGION}-${PROJECT_ID}.cloudfunctions.net/crmScanFileProxy`);
  url.searchParams.set("scanId", String(scanId));
  url.searchParams.set("fileId", String(fileId));
  if (extract) url.searchParams.set("extract", String(extract));
  return url.toString();
}

export async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error("Sign in to view scans.");
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

/** Options: `extract` (zip entry), `signal` (AbortSignal: stops the download). */
async function fetchScanFileResponse(scanId, fileId, { extract, signal } = {}) {
  const res = await fetch(scanFileProxyUrl(scanId, fileId, { extract }), {
    headers: await authHeaders(),
    signal,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(detail || `Failed to load file (${res.status})`);
  }
  return res;
}

export async function fetchScanFileText(scanId, fileId, options = {}) {
  const res = await fetchScanFileResponse(scanId, fileId, options);
  return res.text();
}

export async function fetchScanFileArrayBuffer(scanId, fileId, options = {}) {
  const res = await fetchScanFileResponse(scanId, fileId, options);
  return res.arrayBuffer();
}

function mimeTypeFromFilename(filename) {
  const lower = String(filename || "").toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  return null;
}

function resolveBlobMimeType({ buffer, headerType, hintedType, options } = {}) {
  const sniffed = buffer ? sniffImageMime(buffer) : null;
  if (sniffed) return sniffed;
  const header = String(headerType || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
  if (header.startsWith("image/")) return header;
  if (hintedType) return hintedType;
  return guessBlobMimeType(options);
}

/** Raw bytes of a scan image (decode later with `decodeImageBitmap`). */
export async function fetchScanFileImageBuffer(scanId, fileId, options = {}) {
  const res = await fetchScanFileResponse(scanId, fileId, options);
  return res.arrayBuffer();
}

/** Options: `extract` (zip entry), `maxEdge` (decode downscaled), `maxBytes`. */
export async function fetchScanFileImageBitmap(scanId, fileId, options = {}) {
  const buffer = await fetchScanFileImageBuffer(scanId, fileId, options);
  return decodeImageBitmap(buffer, options);
}

export async function fetchScanFileBlobUrl(scanId, fileId, options = {}) {
  const res = await fetchScanFileResponse(scanId, fileId, options);
  const buffer = await res.arrayBuffer();
  const headerType = res.headers.get("content-type") || "";
  const hintedType = options.mimeType || mimeTypeFromFilename(options.filename) || "";
  const type = resolveBlobMimeType({ buffer, headerType, hintedType, options });
  const blob = new Blob([buffer], { type });
  return URL.createObjectURL(blob);
}

function guessBlobMimeType({ extract } = {}) {
  const extractKey = String(extract || "").toLowerCase();
  if (extractKey === "jpg" || extractKey === "jpeg") return "image/jpeg";
  if (extractKey === "png") return "image/png";
  return "application/octet-stream";
}

export async function loadContactScans(firebaseUid) {
  const fn = crmGetContactScansCallable();
  const res = await fn({ firebaseUid: String(firebaseUid) });
  return res.data?.scans || [];
}

export async function loadScanViewerData(scanId, { meshFileId } = {}) {
  const fn = crmGetScanViewerDataCallable();
  const payload = { scanId: String(scanId), viewerApi: SCAN_VIEWER_API };
  if (meshFileId) payload.meshFileId = String(meshFileId);
  const res = await fn(payload);
  return res.data;
}

export function scanRowFromApi(scan) {
  const capturedAt = scan.captured_at ? new Date(scan.captured_at).getTime() : null;
  const sqft = scan.area_sq_ft ?? scan.areaSqFt ?? null;
  return {
    id: scan.scan_id,
    label: scan.scan_name || scan.scan_id?.slice(0, 10) || "Scan",
    workspaceId: scan.workspace_id || "",
    status: scan.status != null ? String(scan.status) : "",
    sqft,
    sqftLabel: formatSqft(sqft),
    capturedAt,
    visibleFileCount: scan.visible_file_count ?? 0,
    meshFileCount: scan.mesh_file_count ?? 0,
    hasViewableMesh: Boolean(scan.has_viewable_mesh),
    thumbnailFileId: scan.thumbnail_file_id || null,
    viewerMessage: scan.viewer_message || null,
    isV2: Boolean(scan.is_v2),
    chunkCount: scan.chunk_count ?? 0,
    apiError: scan.api_error || null,
    source: scan.source === "firestore_file" ? "firestore_file" : "scan",
    workspaceName: scan.workspace_name || "",
    fileId: scan.file_id || null,
    role: scan.role || "",
  };
}

export function callableErrorMessage(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  const detail = err.details;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return err.message || String(err);
}

export function contactScansFromCollection(scans = [], contact) {
  if (!contact) return [];
  const keys = contactUserKeysForRollup(contact);
  return scans
    .filter((scan) => keys.has(productDocUserRef(scan)))
    .filter((scan) => !scan.deleted_at)
    .sort((a, b) => (scanDocTimestampMs(b) ?? 0) - (scanDocTimestampMs(a) ?? 0));
}

export function contactFilesFromCollection(files = [], contact) {
  if (!contact) return [];
  const keys = contactUserKeysForRollup(contact);
  return files
    .filter((file) => keys.has(productDocUserRef(file)))
    .filter((file) => !file.deleted_at && !file.deletedAt)
    .sort((a, b) => (scanDocTimestampMs(b) ?? 0) - (scanDocTimestampMs(a) ?? 0));
}

function isImageUploadDoc(doc) {
  const name = String(doc?.filename || doc?.file_name || doc?.name || "").toLowerCase();
  const type = String(doc?.content_type || doc?.file_type || "").toLowerCase();
  return (
    type.includes("image") ||
    [".jpg", ".jpeg", ".png", ".webp", ".heic"].some((ext) => name.endsWith(ext) || type.endsWith(ext))
  );
}

export function scanRowFromFileDoc(doc) {
  const sqft = extractSqftFromDoc(doc);
  return {
    id: `file:${doc.id}`,
    label: doc.filename || doc.file_name || doc.name || "Uploaded file",
    workspaceId: workspaceIdFromDoc(doc),
    status: "uploaded",
    sqft,
    sqftLabel: formatSqft(sqft),
    capturedAt: scanDocTimestampMs(doc),
    source: "firestore_file",
    thumbnailFileId: isImageUploadDoc(doc) ? doc.id : null,
    hasViewableMesh: false,
    viewerMessage: "Workspace upload (photo/file) — no 3D scan document was written.",
    visibleFileCount: 1,
    meshFileCount: 0,
    chunkCount: 0,
    isV2: false,
    role: doc.role != null ? String(doc.role) : "",
  };
}

export function scanRowFromDoc(doc) {
  const sqft = extractSqftFromDoc(doc);
  return {
    id: doc.id,
    label: scanLabelFromDoc(doc),
    workspaceId: workspaceIdFromDoc(doc),
    status: doc.status != null ? String(doc.status) : "",
    sqft,
    sqftLabel: formatSqft(sqft),
    capturedAt: scanDocTimestampMs(doc),
    source: "scan",
  };
}

export function scanStatusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "ready") return "badge-green";
  if (s === "processing") return "badge-amber";
  if (s === "draft" || s === "captured") return "badge-muted";
  if (s === "archived") return "badge-muted";
  return "badge-muted";
}

export function scanStatusLabel(status) {
  const s = String(status || "").trim();
  return s ? s.replace(/_/g, " ") : "unknown";
}

export function isScanLikelyOrderReady(status) {
  return String(status || "").toLowerCase() === "ready";
}
